import type { ComponentType } from 'react';
import {
  BookOpenIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  CloudArrowDownIcon,
  CloudIcon,
  EnvelopeIcon,
  FolderIcon,
  LinkIcon,
  ServerStackIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

type SyncConfigLike = Record<string, any> | null | undefined;

export interface ConnectorCatalogEntry {
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  behavior: 'sync' | 'connection';
  supportsSchedule: boolean;
  supportsManualSync: boolean;
}

const FALLBACK_ENTRY: ConnectorCatalogEntry = {
  label: 'Connection',
  description: 'Saved connection configuration',
  icon: LinkIcon,
  behavior: 'connection',
  supportsSchedule: false,
  supportsManualSync: false,
};

export const CONNECTOR_CATALOG: Record<string, ConnectorCatalogEntry> = {
  people_data_labs: {
    label: 'People Data Labs',
    description: 'Professional profile and company data',
    icon: CloudIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  greenhouse: {
    label: 'Greenhouse ATS',
    description: 'ATS candidate and job data',
    icon: BuildingOfficeIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  hubspot: {
    label: 'HubSpot',
    description: 'CRM contacts, companies, and deals',
    icon: BuildingOfficeIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  operating: {
    label: 'Operating',
    description: 'Projects, positions, and allocations',
    icon: BriefcaseIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  fathom: {
    label: 'Fathom',
    description: 'Meeting transcripts and summaries',
    icon: LinkIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  granola: {
    label: 'Granola',
    description: 'Meeting notes and transcripts via browser OAuth',
    icon: LinkIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  filesystem: {
    label: 'Local Filesystem',
    description: 'Local directory scanning for docs or resumes',
    icon: FolderIcon,
    behavior: 'sync',
    supportsSchedule: false,
    supportsManualSync: true,
  },
  s3: {
    label: 'S3 / MinIO',
    description: 'Bucket-backed storage for tenant blobs and applet data sources',
    icon: CircleStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  redshift: {
    label: 'Amazon Redshift',
    description: 'Analytical data warehouse for BI and cashflow applets',
    icon: ServerStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  postgres: {
    label: 'PostgreSQL',
    description: 'PostgreSQL database for governed Agent Mesh Text-to-SQL',
    icon: ServerStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  mariadb: {
    label: 'MariaDB',
    description: 'MariaDB database with registry-discovered tables for live Agent Mesh Text-to-SQL',
    icon: ServerStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  erp: {
    label: 'ERP',
    description: 'Selected ERP tables synced into erp_analytics for Agent Mesh',
    icon: CircleStackIcon,
    behavior: 'connection',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  bigquery: {
    label: 'BigQuery',
    description: 'Google BigQuery warehouse for governed workspace analytics (Text-to-SQL)',
    icon: CircleStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  snowflake: {
    label: 'Snowflake',
    description: 'Snowflake warehouse for structured queries with shared Text-to-SQL engine',
    icon: CircleStackIcon,
    behavior: 'connection',
    supportsSchedule: false,
    supportsManualSync: false,
  },
  atlan: {
    label: 'Atlan',
    description: 'Tenant catalog index, glossary, lineage enrichment, and governed metadata writeback',
    icon: BookOpenIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: false,
  },
  slack: {
    label: 'Slack',
    description: 'Public channel messages from Slack',
    icon: ChatBubbleLeftRightIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  google_drive: {
    label: 'Google Drive',
    description: 'Shared folder and shared drive files from Google Drive',
    icon: CloudArrowDownIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  google_sheets: {
    label: 'Google Sheets',
    description: 'Spreadsheet rows from Google Sheets',
    icon: TableCellsIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
  gmail: {
    label: 'Gmail',
    description: 'Message metadata and snippets from Gmail',
    icon: EnvelopeIcon,
    behavior: 'sync',
    supportsSchedule: true,
    supportsManualSync: true,
  },
};

function formatConnectorTypeLabel(connectorType: string | null | undefined): string {
  const normalizedConnectorType = String(connectorType ?? '').trim();
  if (!normalizedConnectorType) {
    return FALLBACK_ENTRY.label;
  }

  return normalizedConnectorType
    .split('_')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

export function getConnectorCatalogEntry(connectorType: string | null | undefined): ConnectorCatalogEntry {
  return (connectorType ? CONNECTOR_CATALOG[connectorType] : undefined) ?? {
    ...FALLBACK_ENTRY,
    label: formatConnectorTypeLabel(connectorType),
  };
}

export function getConnectorLabel(connectorType: string | null | undefined): string {
  return getConnectorCatalogEntry(connectorType).label;
}

export function getConnectorRequiredPermissions(syncConfig: SyncConfigLike): string[] {
  const permissions = syncConfig?.required_permissions;
  if (!Array.isArray(permissions)) {
    return [];
  }

  return permissions
    .filter((permission): permission is string => typeof permission === 'string')
    .map((permission) => permission.trim())
    .filter(Boolean);
}

export function getConnectorScopeLabel(syncConfig: SyncConfigLike): string {
  const purpose = String(syncConfig?.connection_purpose || '').trim().toLowerCase();
  const scopeType = String(syncConfig?.scope_type || '').trim().toLowerCase();
  const scopeTarget = String(syncConfig?.scope_target || '').trim();

  if (syncConfig?.use_for_tenant_storage || purpose === 'tenant_object_storage') {
    return 'Tenant object storage';
  }
  if (scopeType === 'applet' && scopeTarget) {
    return `Applet: ${scopeTarget}`;
  }
  if (scopeType === 'applet') {
    return 'Applet-scoped';
  }
  return 'Tenant-wide';
}

export function getConnectorSummary(
  connectorType: string,
  syncConfig: SyncConfigLike,
): string | null {
  if (!syncConfig) {
    return null;
  }

  if (connectorType === 's3') {
    const backend = String(syncConfig.backend || 's3').toUpperCase();
    const bucket = String(syncConfig.bucket || '').trim();
    const prefix = String(syncConfig.path_prefix || '').trim();
    const parts = [backend];

    if (bucket) {
      parts.push(bucket);
    }
    if (prefix) {
      parts.push(`/${prefix}`);
    }

    return parts.join(' • ');
  }

  if (connectorType === 'filesystem') {
    const directory = String(syncConfig.directory_path || '').trim();
    return directory || 'Local directory';
  }

  if (connectorType === 'greenhouse') {
    const maxCandidates = syncConfig.max_candidates;
    if (maxCandidates) {
      return `Up to ${maxCandidates} candidates per sync`;
    }
    return 'ATS candidate sync';
  }

  if (connectorType === 'people_data_labs') {
    const searchQuery =
      syncConfig.search_query && typeof syncConfig.search_query === 'object'
        ? syncConfig.search_query
        : {};
    const filterCount = Object.keys(searchQuery).length;
    return filterCount > 0 ? `${filterCount} search filters configured` : 'PDL search query';
  }

  if (connectorType === 'hubspot') {
    const objectType = String(syncConfig.object_type || '').trim();
    return objectType ? `Syncing ${objectType}` : 'CRM sync connection';
  }

  if (connectorType === 'operating') {
    const baseUrl = String(syncConfig.base_url || syncConfig.api_base_url || '').trim();
    return baseUrl || 'Operating API connection';
  }

  if (connectorType === 'fathom') {
    const limit = syncConfig.limit;
    return limit ? `Meeting limit: ${limit}` : 'Meeting intelligence connection';
  }

  if (connectorType === 'slack') {
    const channelIds = Array.isArray(syncConfig.channel_ids) ? syncConfig.channel_ids : [];
    return channelIds.length > 0 ? `${channelIds.length} channels configured` : 'Slack channel sync';
  }

  if (connectorType === 'granola') {
    return 'Live retrieval via browser OAuth';
  }

  if (connectorType === 'redshift') {
    const host = String(syncConfig.host || '').trim();
    const database = String(syncConfig.database || '').trim();
    const schema = String(syncConfig.schema || '').trim();
    const parts: string[] = [];
    if (host) parts.push(host);
    if (database) parts.push(database);
    if (schema) parts.push(schema);
    return parts.length > 0 ? parts.join(' • ') : 'Redshift connection';
  }

  if (connectorType === 'bigquery') {
    const project = String(syncConfig.project_id || syncConfig.project || '').trim();
    return project ? `Project ${project}` : 'BigQuery warehouse connection';
  }

  if (connectorType === 'erp') {
    const provider = String(syncConfig.provider || 'erpnext').trim();
    const baseUrl = String(syncConfig.base_url || '').trim();
    const companyId = String(syncConfig.company_id || '').trim();
    return [provider.toUpperCase(), baseUrl, companyId].filter(Boolean).join(' • ') || 'ERP connection';
  }

  if (connectorType === 'snowflake') {
    const account = String(syncConfig.account || syncConfig.account_locator || '').trim();
    const database = String(syncConfig.database || '').trim();
    const parts = [account, database].filter(Boolean);
    return parts.length > 0 ? parts.join(' • ') : 'Snowflake connection';
  }

  if (connectorType === 'atlan') {
    const url = String(syncConfig.base_url || syncConfig.instance_url || '').trim();
    return url || 'Atlan catalog connector';
  }

  if (connectorType === 'google_drive') {
    const link = String(syncConfig.shared_folder_link || '').trim();
    const fid = String(syncConfig.folder_id || '').trim();
    if (link) return link.length > 48 ? `${link.slice(0, 45)}...` : link;
    if (fid) return `Folder ${fid}`;
    return 'Google Drive shared folder';
  }

  if (connectorType === 'google_sheets') {
    const link = String(syncConfig.spreadsheet_link || '').trim();
    const id = String(syncConfig.spreadsheet_id || '').trim();
    if (link) return link.length > 48 ? `${link.slice(0, 45)}…` : link;
    if (id) return `Spreadsheet ${id}`;
    return 'Google Sheets spreadsheet';
  }

  if (connectorType === 'gmail') {
    const query = String(syncConfig.query || '').trim();
    return query ? `Query: ${query}` : 'Gmail message sync';
  }

  return null;
}

export function isConnectorVisibleToApplet(
  syncConfig: SyncConfigLike,
  appletKey: string,
  hasAllPermissions?: (permissions: string[]) => boolean,
): boolean {
  const purpose = String(syncConfig?.connection_purpose || '').trim().toLowerCase();
  if (purpose === 'tenant_object_storage') {
    return false;
  }

  const scopeType = String(syncConfig?.scope_type || '').trim().toLowerCase();
  const scopeTarget = String(syncConfig?.scope_target || '').trim();
  if (scopeType === 'applet' && scopeTarget && scopeTarget !== appletKey) {
    return false;
  }

  const requiredPermissions = getConnectorRequiredPermissions(syncConfig);
  if (requiredPermissions.length > 0 && hasAllPermissions && !hasAllPermissions(requiredPermissions)) {
    return false;
  }

  return true;
}
